import { LocalDate, DateTimeFormatter } from "@js-joda/core";
import moment from "moment";

import {
    declareAiCapability,
    provideAiCapability,
} from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-capability-registry";

// Clock in & out describing its own filter bar. The page opens on today, so a request without dates
// keeps today rather than quietly widening to everything.
declareAiCapability({
    id: "clockedVisits.filter",
    title: "Filter the clock in & out list",
    effect: "prefill",
    description:
        "Sets the filters on EVV > Clock in & out and re-runs its search: the visit date range, contract and office, plus a free-text search over the table. The page opens on today, and anything left out keeps what it already has. Only works while that page is open.",
    location: "the filter bar of the EVV > Clock in & out page",
    scope: "page",
    permissionKey: "view_evv_clock_in_out",
    params: [
        { name: "from", type: "date", required: false, description: "First visit day to include, as yyyy-MM-dd." },
        { name: "to", type: "date", required: false, description: "Last visit day to include, as yyyy-MM-dd." },
        {
            name: "contract",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getClockedVisitsContractOptions(),
            description: "Limit to these contracts."
        },
        {
            name: "office",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getClockedVisitsOfficeOptions(),
            description: "Limit to these offices."
        },
        {
            name: "search",
            type: "text",
            required: false,
            searchesFreeText: true,
            description: "Free text the table searches over every column, including caregiver and patient names."
        }
    ],
    examples: [
        "clock ins last week",
        "clock in and out for the europe office yesterday",
        "clocked visits for the france contract"
    ]
});

let clockedVisitsContractTypes = () => [];
let clockedVisitsOffices = () => [];

function getClockedVisitsContractOptions() {
    return clockedVisitsContractTypes().map((contract) => ({ value: String(contract.id), label: contract.name }));
}

function getClockedVisitsOfficeOptions() {
    return clockedVisitsOffices().map((office) => ({ value: String(office.id), label: office.name }));
}

//! @ngInject
export function clockedVisitsCtrl(
    $scope,
    NgTableParams,
    itemSearchPageManager,
    toaster,
    $rootScope,
    $filter,
    DatabaseApi,
    generalUtils,
    wildcard,
    mfModal
  ) {
    const agencyBranches = DatabaseApi.agencyBranches() || [];
    $scope.agencyBranches = agencyBranches.map((branch) => ({
      id: branch.id,
      label: branch.name,
    }));
    $scope.contractTypes = DatabaseApi.contractTypes();
    $scope.coordinators = [];

    const mapCertificationOptions = () =>
      (DatabaseApi.activeAgencyCertifications() || []).map((certificationItem, index) => ({
        id: index,
        label: certificationItem.certification,
      }));
    $scope.certifications = mapCertificationOptions();

    $scope.tableParams = null;
    $scope.clockMethods = [
      { id: "ALL", label: "All" },
      { id: "MOBILE", label: "Mobile" },
      { id: "TEL", label: "Tel" },
      { id: "IVR", label: "IVR" },
      { id: "MANUAL", label: "Manual" },
      { id: "TIME_SHEET", label: "Time Sheet" },
    ];
    $scope.filters = {
      clockMethod: "ALL",
      incompleteVisits: false,
      evvErrorSubmission: false,
      missedVisits: false,
      agencyBranches: [],
      contracts: [],
      offices: [],
      coordinators: [],
      teams: [],
      certifications: [],
    };
    $scope.globalFilter = { val: "" };

    $scope.filteredVisits = [];

    $scope.tableColumns = {
      VisitDate: true,
      VisitTime: true,
      Patient: true,
      Caregiver: true,
      ClockIn: true,
      ClockOut: true,
      ClockInType: true,
      ClockOutType: true,
      ClockInLocation: true,
      ClockOutLocation: true,
      TimelyFiling: true
    };

    $scope.multiSelectComponentOptions = {
      styleActive: true,
      scrollable: true,
      scrollableHeight: "250px",
      enableSearch: true,
    };

    $scope.selfieComparisonCache = {};

    const evvClassesMap = {
      SUBMITTED: 'fa fa-check text-blue',
      FAILED: 'fa fa-times text-danger',
      PENDING: 'fa fa-clock-o text-warning',
    };

    const clockSelfieClassesMap = {
      SUBMITTED: 'fa fa-check text-blue',
      FAILED: 'fa fa-times text-danger',
      PENDING: 'fa fa-clock-o text-warning',
      MISSING: 'fa fa-question text-danger',
      NOT_REQUIRED: 'fa fa-minus text-black'
    };

    /**
     * 
     * @param {number} visitInstanceId 
     * @param {"IN" | "OUT"} clockType 
     */
    $scope.showSelfieComparison = async (visitInstanceId, clockType) => {
      let data = $scope.selfieComparisonCache[`${visitInstanceId}-${clockType}`];
      if (!data) {
        const { agencyId, agencyMemberId } = $rootScope;
        const url = wildcard(
          "agencies/:agencyId/agency_members/:agencyMemberId/visit_instances/:visitInstanceId/selfie_compare/:clockType",
          agencyId,
          agencyMemberId,
          visitInstanceId,
          clockType
        );
        const res = await DatabaseApi.get(url);
        $scope.selfieComparisonCache[`${visitInstanceId}-${clockType}`] = res.data;
        data = res.data;
      }

      const modal = mfModal.create({
        subject: "Face Comparison",
        variant: "info",
        customTemplate: "comparison-modal-content",
        message: `Compare the images below to confirm the clock ${clockType.toLowerCase()} selfie is valid.`,
        confirmLabel: "Close",
        size: "xl",
        layoutOrder: ["message", "customTemplate", "input"],
        showInput: false,
        hideCancelButton: true,
        preventBackdropClose: false,
        data,
        onConfirm: () => {
          modal.close();
        }
      });
    }

    const initialize = () => {
      initPageManager();
      $scope.loadItems();
      setTimeout(() => {
        initCoordinatorDropdown();
      }, 5000);
    };

    const initCoordinatorDropdown = () => {
      const currentAgencyMemberOfficeIds = $rootScope.user.agencyMember.officeIds;
      const agencyMembersByRoles = DatabaseApi.getAgencyMembersByRole() || {};
      $scope.coordinators = [
        ...(agencyMembersByRoles.coordinatorsSeniors || []),
        ...(agencyMembersByRoles.coordinators || []),
        ...(agencyMembersByRoles.admins || []),
      ]
        .sort(generalUtils.sortByDisplayName)
        .filter((coordinator) =>
          coordinator.officeIds.findIndex((coordinatorOfficeId) => {
            return currentAgencyMemberOfficeIds.includes(coordinatorOfficeId);
          }) !== -1
        );
    };

    const initPageManager = () => {
      $scope.pageManager = itemSearchPageManager.createSearchPageManager("/clocked_visits");

      $scope.pageManager.initFromToDateParams();
      $scope.pageManager.updateSearchParamValue("from", new Date(LocalDate.now().format(DateTimeFormatter.ofPattern("MM/dd/yyyy"))));
      $scope.pageManager.updateSearchParamValue("to", new Date(LocalDate.now().format(DateTimeFormatter.ofPattern("MM/dd/yyyy"))));
      $scope.pageManager.initContractTypesParam();
      $scope.pageManager.initOfficeParam();
      $scope.contractTypesDataManager = $scope.pageManager.getContractTypesDataManager();
      $scope.officeDataManager = $scope.pageManager.getOfficeDataManager();
      $scope.coordinatorDataManager = $scope.pageManager.getCoordinatorDataManager({onlyActive: true});
      $scope.teamDataManager = $scope.pageManager.getTeamDataManager();
    };

    const setClockInSelfieStatusParams = (item) => {
      if (!item.clockInSelfieStatus || item.clockInSelfieStatus.type === "NotRequired") {
        item.clockInSelfieClasses = clockSelfieClassesMap.NOT_REQUIRED;
        item.clockInSelfieText = "Not Required";
        return item;
      }

      const clockInSelfieClasses = (() => {
        if (item.clockInSelfieStatus.result === null && item.clockInSelfieStatus.s3Key === null) {
          return clockSelfieClassesMap.MISSING;
        }
        switch (item.clockInSelfieStatus.result) {
          case null:
            return clockSelfieClassesMap.PENDING;
          case "VALID":
            return clockSelfieClassesMap.SUBMITTED;
          case "INVALID":
          case "BELOW_THRESHOLD":
          case "ERROR":
          case "SMALL_IMAGE":
          default:
            return clockSelfieClassesMap.FAILED;
        }
      })();

      const clockInSelfieText = (() => {
        if (item.clockInSelfieStatus.result === null && item.clockInSelfieStatus.s3Key === null) {
          return "Missing";
        }
        if (item.clockInSelfieStatus.result === null) {
          return "Pending Result";
        }
        if (item.clockInSelfieStatus.error !== null) {
          return item.clockInSelfieStatus.error;
        }
        return item.clockInSelfieStatus.result;
      })();

      item.clockInSelfieClasses = clockInSelfieClasses,
      item.clockInSelfieText = clockInSelfieText
      return item;
    }

    const setClockOutSelfieStatusParams = (item) => {
      if (!item.clockInSelfieStatus || item.clockOutSelfieStatus.type === "NotRequired") {
        item.clockOutSelfieClasses = clockSelfieClassesMap.NOT_REQUIRED;
        item.clockOutSelfieText = "Not Required";
        return item;
      }

      const clockInSelfieClasses = (() => {
        if (item.clockOutSelfieStatus.result === null && item.clockOutSelfieStatus.s3Key === null) {
          return clockSelfieClassesMap.MISSING;
        }
        switch (item.clockOutSelfieStatus.result) {
          case null:
            return clockSelfieClassesMap.PENDING;
          case "VALID":
            return clockSelfieClassesMap.SUBMITTED;
          case "INVALID":
          case "BELOW_THRESHOLD":
          case "ERROR":
          case "SMALL_IMAGE":
          default:
            return clockSelfieClassesMap.FAILED;
        }
      })();

      const clockOutSelfieText = (() => {
        if (item.clockOutSelfieStatus.result === null && item.clockOutSelfieStatus.s3Key === null) {
          return "Missing";
        }
        if (item.clockOutSelfieStatus.result === null) {
          return "Pending Result";
        }
        if (item.clockOutSelfieStatus.error !== null) {
          return item.clockOutSelfieStatus.error;
        }
        return item.clockOutSelfieStatus.result;
      })();

      item.clockOutSelfieClasses = clockInSelfieClasses,
      item.clockOutSelfieText = clockOutSelfieText
      return item;
    }

    const populateTable = () => {
      const oldTotal = $scope.tableParams?.total?.() || 0;
      let sorting = { id: "desc" };
      let page = false;
      let count = 25;
      if ($scope.tableParams) {
        sorting = $scope.tableParams.sorting();
        page = $scope.tableParams.page();
        count = $scope.tableParams.count();
      }
      const options = {
        count: count,
        sorting: sorting,
      };

      const parseTellusEvvDetails = (item) => {
        const evvSubmissionDetails = item.evvSubmissionDetails;
        if (evvSubmissionDetails.errorType !== null) {
          item.evvStatus = "FAILED";
          item.evvClasses = evvClassesMap[item.evvStatus];
          if (evvSubmissionDetails.errors !== null) {
            item.evvText = "Sent On: " +
              $filter("date")(evvSubmissionDetails.sentAt, 'd MMM, yyyy') + " " +
              $filter("mfShortTime")(evvSubmissionDetails.sentAt) + " " +
              evvSubmissionDetails.errorType + ": " + evvSubmissionDetails.errors.join(',&#013;'); 
          }
        } else {
          item.evvStatus = "SUBMITTED";
          item.evvClasses = evvClassesMap[item.evvStatus];
          item.evvText = "Sent On: " +
          $filter("date")(evvSubmissionDetails.sentAt, 'd MMM, yyyy') + " " +
          $filter("mfShortTime")(evvSubmissionDetails.sentAt)
        }

        return item;
      };

      const parseEmednyEvvDetails = (item) => {
        const evvSubmissionDetails = item.evvSubmissionDetails;
        if (evvSubmissionDetails.submitted === true) {
          item.evvStatus = 'SUBMITTED';
          item.evvClasses = evvClassesMap[item.evvStatus];
          item.evvText = "Sent On: " +
            $filter("date")(evvSubmissionDetails.processedAt, 'd MMM, yyyy') + " " +
            $filter("mfShortTime")(evvSubmissionDetails.processedAt) + " " +
            " Transaction Id: " + evvSubmissionDetails.transactionId;
        } else {
          // evvSubmissionDetails.submitted === false
          item.evvStatus = 'FAILED';
          item.evvClasses = evvClassesMap[item.evvStatus];
          if (evvSubmissionDetails.processedAt && evvSubmissionDetails.errorType) {
            item.evvText = "Sent On: " +
              $filter("date")(evvSubmissionDetails.processedAt, 'd MMM, yyyy') + " " +
              $filter("mfShortTime")(evvSubmissionDetails.processedAt) + " " +
              evvSubmissionDetails.errorType + ": " + evvSubmissionDetails.errors.join(',&#013;');
          }
        }

        return item;
      };

      const parseSandataEvDetails = (item) => {
        const evvSubmissionDetails = item.evvSubmissionDetails;

        /**
         * error - means failure
         */
        if (Array.isArray(evvSubmissionDetails.errors)) {
          item.evvStatus = "FAILED";
          item.evvClasses = evvClassesMap[item.evvStatus];
          item.evvText = "Sent On: " +
            $filter("date")(evvSubmissionDetails.submittedAt, 'd MMM, yyyy') + " " +
            $filter("mfShortTime")(evvSubmissionDetails.submittedAt) +
            ": " + evvSubmissionDetails.errors.join(', ');
          return item;
        }


        /**
         * submitted and completed - means success
         */
        if (evvSubmissionDetails.submittedAt !== null && evvSubmissionDetails.completed) {
          item.evvStatus = "SUBMITTED";
          item.evvClasses = evvClassesMap[item.evvStatus];
          item.evvText = "Sent On: " +
          $filter("date")(evvSubmissionDetails.submittedAt, 'd MMM, yyyy') + " " +
          $filter("mfShortTime")(evvSubmissionDetails.submittedAt)
          return item;
        }

        /**
         * else is pending because of worker error or just because
         */
        item.evvStatus = "PENDING";
        item.evvClasses = evvClassesMap[item.evvStatus];
        item.evvText = "Pending submission";

        return item;
      }

      const parseHHAExchangeEvvDetails = (item) => {
        const evvSubmissionDetails = item.evvSubmissionDetails;

        const sentOnText = evvSubmissionDetails.sentAt
          ? "Sent On: " +
            $filter("date")(evvSubmissionDetails.sentAt, 'd MMM, yyyy') + " " +
            $filter("mfShortTime")(evvSubmissionDetails.sentAt)
          : null;
        const transactionIdText = evvSubmissionDetails.transactionId
          ? " Transaction Id: " + evvSubmissionDetails.transactionId
          : "";

        if (evvSubmissionDetails.errorType !== null) {
          item.evvStatus = "FAILED";
          item.evvClasses = evvClassesMap[item.evvStatus];
          const errorBody = Array.isArray(evvSubmissionDetails.errors)
            ? evvSubmissionDetails.errors.join(',&#013;')
            : "";
          item.evvText = (sentOnText ? sentOnText + " " : "") +
            evvSubmissionDetails.errorType + ": " + errorBody +
            transactionIdText;
          return item;
        }

        if (evvSubmissionDetails.submitted === true) {
          item.evvStatus = "SUBMITTED";
          item.evvClasses = evvClassesMap[item.evvStatus];
          item.evvText = (sentOnText || "Submitted") + transactionIdText;
          return item;
        }

        item.evvStatus = "PENDING";
        item.evvClasses = evvClassesMap[item.evvStatus];
        item.evvText = "Pending submission" + transactionIdText;
        return item;
      }

      $scope.filteredVisits.forEach((item) => {
        // set clock time bounds
        if (item.clockinTime) {
          item.isClockInTimeOverdue = isClockTimeOutOfBound({
            scheduledTime: item.startTime,
            clockTime: item.clockinTime,
            patientContractType: item.patientContractType,
            clockType: "IN"
          });
        }
        if (item.clockoutTime) {
          item.isClockOutTimeOverdue = isClockTimeOutOfBound({
            scheduledTime: item.endTime,
            clockTime: item.clockoutTime,
            patientContractType: item.patientContractType,
            clockType: "OUT"
          });
        }

        item = setClockInSelfieStatusParams(item);
        item = setClockOutSelfieStatusParams(item);

        // emedny text
        switch (item.evvSubmissionDetails?.type) {
            case "EmedNy": {
              item = parseEmednyEvvDetails(item);
              break;
            }
            case "Tellus": {
              item = parseTellusEvvDetails(item)
              break;
            }
            case "Sandata": {
              item = parseSandataEvDetails(item);
              break;
            }
            case "HHAExchange - SFTP":
            case "HHAExchange - API": {
              item = parseHHAExchangeEvvDetails(item);
              break;
            }
            default: {
              item.evvStatus = 'PENDING';
              item.evvClasses = evvClassesMap[item.evvStatus];
              item.evvText = "Pending submission";
            }
        }
      });

      $scope.tableParams = new NgTableParams(options, {
        dataset: $scope.searchText
          ? $filter("filter")($scope.filteredVisits, $scope.searchText)
          : $scope.filteredVisits,
      });
      if (page && oldTotal === $scope.tableParams.total()) $scope.tableParams.page(page);
      if ($scope.globalFilter) {
        $scope.applyGlobalSearch($scope.globalFilter.val);
      }
    };


    clockedVisitsContractTypes = () => $scope.contractTypesDataManager?.getActiveContractTypes() ?? [];
    clockedVisitsOffices = () => $scope.officeDataManager?.getOffices() ?? [];

    const releaseClockedVisitsFilter = provideAiCapability("clockedVisits.filter", (args) => {
      const applied = [];
      const asList = (value) => (Array.isArray(value) ? value : [value]);
      const asDate = (text) => {
        const parts = String(text).split("-").map(Number);

        if (parts.length !== 3 || parts.some(isNaN)) {
          throw new Error("Could not read the date " + text + ".");
        }

        return new Date(parts[0], parts[1] - 1, parts[2]);
      };
      const pickAll = (available, requested, what) =>
        asList(requested).map((one) => {
          const found = (available ?? []).find((item) => String(item.id) === String(one));

          if (found === undefined) {
            throw new Error("This agency has no " + what + " " + one + ".");
          }

          return found;
        });

      if (args.from !== undefined) {
        $scope.pageManager.searchParams.from.value = asDate(args.from);
      }

      if (args.to !== undefined) {
        $scope.pageManager.searchParams.to.value = asDate(args.to);
      }

      if (args.from !== undefined || args.to !== undefined) {
        applied.push([args.from, args.to].filter(Boolean).join(" to "));
      }

      if (args.contract !== undefined) {
        const contracts = pickAll(clockedVisitsContractTypes(), args.contract, "contract");

        $scope.pageManager.searchParams.selectedContractTypes.value = contracts;
        applied.push(contracts.map((contract) => contract.name).join(" and "));
      }

      if (args.office !== undefined) {
        const offices = pickAll(clockedVisitsOffices(), args.office, "office");

        $scope.pageManager.searchParams.selectedOffices.value = offices;
        applied.push(offices.map((office) => office.name).join(" and "));
      }

      // Set before the reload: the page re-applies globalFilter once the rows land.
      if (args.search !== undefined) {
        $scope.globalFilter.val = String(args.search);
        applied.push('"' + String(args.search) + '"');
      }

      $scope.loadItems();

      return applied.length === 0
        ? "Re-ran the clock in & out search"
        : "Showing clock in & out: " + applied.join(", ");
    });

    $scope.$on("$destroy", releaseClockedVisitsFilter);

    $scope.loadItems = () => {
      $scope.pageManager.executeSearch().then((res) => {
          $scope.clockedVisits = setIncomingClockedVisits(res.data.visitInstances);
          $scope.onFiltersChange();
        },
        (err) => {
          toaster.pop("error", "Failed to load clocked visits");
        }
      );
    };

    $scope.onFiltersChange = () => {
      $scope.filteredVisits = $scope.clockedVisits;

      // set incomplete visits
      if ($scope.filters.incompleteVisits) {
        $scope.filteredVisits = $scope.clockedVisits.filter((visit) =>
          visit.clockinTime === null || visit.clockoutTime === null
        );
      }

      // Evv submission
      if ($scope.filters.evvErrorSubmission) {
        $scope.filteredVisits = $scope.filteredVisits.filter((visit) => hasEvvSubmissionError(visit));
      }

      // Missed visits
      if ($scope.filters.missedVisits) {
        $scope.filteredVisits = $scope.filteredVisits.filter((visit) =>
          visit.clockinTime === null && new Date(visit.startTime).valueOf() < new Date().valueOf()
        );
      }

      // // Contracts
      // if ($scope.filters.contracts.length) {
      //   const selectedContractIds = $scope.filters.contracts.map(contract => contract.id);
      //   $scope.filteredVisits = $scope.filteredVisits.filter((visit) =>
      //     visit.patientContractType !== null &&
      //     selectedContractIds.indexOf(visit.patientContractType) !== -1
      //   );
      // }

      // // Offices
      // if ($scope.filters.offices.length) {
      //   const selectedOfficeIds = $scope.filters.offices.map(office => office.id);
      //   $scope.filteredVisits = $scope.filteredVisits.filter(
      //     (visit) =>
      //       visit.officeId !== null &&
      //       selectedOfficeIds.indexOf(visit.officeId) !== -1
      //   );
      // }

      // Branches
      if ($scope.filters.agencyBranches.length) {
        const selectedBranchIds = $scope.filters.agencyBranches.map(branch => branch.id);
        $scope.filteredVisits = $scope.filteredVisits.filter((visit) =>
          visit.caregiverBranches !== null &&
          selectedBranchIds.find((branchId) => visit.caregiverBranches.indexOf(branchId) !== -1) !== undefined
        );
      }

      // Coordinators
      if ($scope.filters.coordinators.length) {
        const selectedCoordinatorIds = $scope.filters.coordinators.map(c => c.id);
        $scope.filteredVisits = $scope.filteredVisits.filter((visit) =>
          visit.assignedCoordinator !== null &&
          selectedCoordinatorIds.indexOf(visit.assignedCoordinator) !== -1
        );
      }

      // Teams
      if ($scope.filters.teams.length) {
        const selectedTeamIds = $scope.filters.teams.map((team) => team.id);
        $scope.filteredVisits = $scope.filteredVisits.filter((visit) =>
          visit.patientAgencyTeam !== null &&
          selectedTeamIds.indexOf(visit.patientAgencyTeam) !== -1
        );
      }

      // Certifications
      if ($scope.filters.certifications.length) {
        const selectedCertifications = $scope.filters.certifications
          .map((selected) => $scope.certifications.find((cert) => cert.id === selected.id))
          .filter((cert) => cert !== undefined)
          .map((cert) => cert.label);
        $scope.filteredVisits = $scope.filteredVisits.filter((visit) =>
          visit.caregiverCertifications.some((certification) =>
            selectedCertifications.includes(certification)
          )
        );
      }

      // clock method
      switch ($scope.filters.clockMethod) {
        case "ALL": {
          populateTable();
          break;
        }
        default: {
          $scope.filteredVisits = $scope.filteredVisits.filter((visit) => {
            return (
              (visit.clockinType === $scope.filters.clockMethod &&
                visit.clockoutType === $scope.filters.clockMethod) ||
              (visit.clockinType === $scope.filters.clockMethod &&
                visit.clockoutType === null) ||
              (visit.clockoutType === $scope.filters.clockMethod &&
                visit.clockinType === null)
            );
          });
          populateTable();
        }
      }
    };

    $scope.exportTable = () => {
      // if (!$scope.table || !$scope.table.data || !$scope.table.data.length) return;
      var tableData = $scope.filteredVisits;

      if (tableData.length <= 0) {
        toaster.pop("warning", "Nothing to export", "The table is empty");
        return;
      }

      var rows = [];
      var titles = [
        "Visit ID",
        "Visit date",
        "Visit time",
        "Patient",
        "Caregiver",
        "Clock In",
        "Clock In notes",
        "Clock Out",
        "Clock Out notes",
        "Clock In type",
        "Clock Out type",
        "Timely Filing",
        "EVV Status",
        "EVV Details",
      ];

      rows.push(titles);

      tableData.forEach((visit) => {
        var row = [];
        titles.forEach((title) => {
          if (title === "Visit ID") row.push(visit.id || "");
          else if (title === "Visit date") row.push($filter("mfShortDate")(visit.startTime) || "");
          else if (title === "Visit time") row.push($filter("mfShortTime")(visit.startTime) + "-" + $filter("mfShortTime")(visit.endTime));
          else if (title === "Patient") row.push(visit.patientFullName || "");
          else if (title === "Caregiver") row.push(visit.caregiverFullName || "");
          else if (title === "Clock In") row.push($filter("mfShortTime")(visit.clockinTime) || "");
          else if (title === "Clock In notes") row.push(visit.isClockInTimeOverdue ? "Clock in time is too far from schedule" : "")
          else if (title === "Clock Out") row.push($filter("mfShortTime")(visit.clockoutTime) || "");
          else if (title === "Clock Out notes") row.push(visit.isClockInTimeOverdue ? "Clock out time is too far from schedule" : "")
          else if (title === "Clock In type") row.push(visit.clockinType || "");
          else if (title === "Clock Out type") row.push(visit.clockoutType || "");
          else if (title === "Timely Filing") row.push(`${visit.filingLimitDays ?? ""} (${visit.filingLimitDaysLeft ?? ""})`);
          else if (title === "EVV Status") row.push(visit.evvStatus || "");
          else if (title === "EVV Details") row.push((visit.evvText || "").replace(/&#013;/g, " | "));
        });
        rows.push(row);
      });

      var csvContent = "data:text/csv;charset=utf-8,";
      rows.forEach((rowArray) => {
        var row = rowArray.map(escapeCsvCell).join(",");
        csvContent += row + "\r\n";
      });

      var encodedUri = encodeURI(csvContent);
      var link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", getExportedFileName());
      document.body.appendChild(link); // Required for FF

      link.click();
    };

    const getExportedFileName = () => {
      const fromDate = $filter("date")(new Date($scope.pageManager.searchParams.from.value), "yyyy-MM-dd");
      const toDate = $filter("date")(new Date($scope.pageManager.searchParams.to.value), "yyyy-MM-dd");
      return `medflyt-clocked-visits-export-${fromDate}-to-${toDate}.csv`;
    };

    const escapeCsvCell = (value) => {
      const stringValue = value === null || value === undefined ? "" : String(value);
      return `"${stringValue.replace(/"/g, '""')}"`;
    };

    const isClockTimeOutOfBound = ({scheduledTime, clockTime, patientContractType, clockType}) => {
      let schedule = moment(scheduledTime);
      let checked = moment(clockTime);

      const contractType = $scope.contractTypes.find(c => c.id === patientContractType);
      const clockTimeDiff = clockType === "IN" ?
        (contractType?.clockInToleranceMinutes ?? 15) :
        (contractType?.clockOutToleranceMinutes ?? 15);

      const minutesDiff = Math.abs(checked.diff(schedule, "minutes"));
      return minutesDiff > clockTimeDiff;
    };

    const setIncomingClockedVisits = (items) => {
      items.forEach((visit) => {
        if (visit.clockinType !== null && visit.clockinTime === null) {
          visit.clockinType = null;
        }
        if (visit.clockoutType !== null && visit.clockoutTime === null) {
          visit.clockoutType = null;
        }
      });

      return items;
    };

    const hasEvvSubmissionError = (visit) => {
      const evvSubmissionDetails = visit?.evvSubmissionDetails;
      if (!evvSubmissionDetails) {
        return false;
      }

      switch (evvSubmissionDetails.type) {
        case "EmedNy":
          return evvSubmissionDetails.submitted === false;
        case "Tellus":
          return evvSubmissionDetails.errorType !== null;
        case "Sandata":
          return Array.isArray(evvSubmissionDetails.errors);
        default:
          return false;
      }
    };

    $scope.applyGlobalSearch = () => {
      const filter = { $: $scope.globalFilter.val };
      if ($scope.tableParams) {
        angular.extend($scope.tableParams.filter(), filter);
      }
    };

    $scope.multiSelectEvents = {
      onSelectionChanged() {
        $scope.onFiltersChange();
      },
    };

    $scope.$on("refresh_visits", $scope.loadItems);

    $rootScope.$on("got_data", () => {
      const agencyBranches = DatabaseApi.agencyBranches() || [];
      $scope.agencyBranches = agencyBranches.map((branch) => ({
        id: branch.id,
        label: branch.name,
      }));
      $scope.certifications = mapCertificationOptions();
    });

    $rootScope.$on("got_contract_types", () => {
      $scope.contractTypes = DatabaseApi.contractTypes();
    });

    initialize();
  };
